import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OrderTable } from '../../Components/OrderTable';
import { obtenerSolicitudActividades } from '../../redux/solicitudesAdmSlice';
import { useParams, useLocation } from 'react-router-dom';
import LoadingSpiner from '../../Components/LoadingSpiner';
import { getAdmissionPage } from '../../Api/content';
import { useLocale } from '../../i18n/localeContext';
import { Skeleton } from 'antd';
import { StrapiError } from '../../Inicio/Pages/StrapiError';
import { _translateValue } from '../../Adm/translation-gestor/translateValue';

const SCREEN_NAME = "PantallaHistorialActividades";

function HistorialActividades() {

  const dispatch = useDispatch();
  const [admissionPage, setAdmissionPage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const { locale } = useLocale();
  const [hasError, setHasError] = useState(false);
  const [translatedRoles, setTranslatedRoles] = useState({});
  const [translatedDescripciones, setTranslatedDescripciones] = useState({});
  const [translatedEstados, setTranslatedEstados] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const { solicitudesActividades, loading } = useSelector(
    (state) => state.solicitudesAdm
  );
  const params = useParams();
  const { state } = useLocation();
  useEffect(() => {
    dispatch(obtenerSolicitudActividades(params.id));
  }, [dispatch, params.id]);

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setAdmissionPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 5000);
    getAdmissionPage(locale)
      .then((res) => {
        clearTimeout(timeoutId);
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdmissionPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch((error) => { 
        clearTimeout(timeoutId);
        if (!cancel) {
          setAdmissionPage(null);
          setHasError(locale === 'en');
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true; 
      clearTimeout(timeoutId); };
  }, [locale]);

  // Efecto para traducir los campos "Rol", "Descripción" y "Estado" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasActividades = solicitudesActividades && Array.isArray(solicitudesActividades) && solicitudesActividades.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasActividades) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const rolesTranslations = {};
      const descripcionTranslations = {};
      const estadoTranslations = {};
      const translationPromises = [];

      // Recopilar todos los valores únicos que necesitan traducción
      solicitudesActividades.forEach((item) => {
        // Traducir rol
        if (item.rol && !rolesTranslations[item.rol]) {
          rolesTranslations[item.rol] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, item.rol, locale).then((translated) => {
              rolesTranslations[item.rol] = translated;
            })
          );
        }
        
        // Traducir descripción
        if (item.descripcion && !descripcionTranslations[item.descripcion]) {
          descripcionTranslations[item.descripcion] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, item.descripcion, locale).then((translated) => {
              descripcionTranslations[item.descripcion] = translated;
            })
          );
        }
        
        // Traducir estado
        if (item.estadoNombre && !estadoTranslations[item.estadoNombre]) {
          estadoTranslations[item.estadoNombre] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, item.estadoNombre, locale).then((translated) => {
              estadoTranslations[item.estadoNombre] = translated;
            })
          );
        }
      });
      
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedRoles(rolesTranslations);
      setTranslatedDescripciones(descripcionTranslations);
      setTranslatedEstados(estadoTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [solicitudesActividades, locale]);

  // Create columns dynamically from activitytable data
  const columns = useMemo(() => {
    const getColumns = () => {
    if (!admissionPage?.activitytable) {
      // Fallback columns if no data is available
      return [
        { label: '#', accessor: 'actividadId', sortable: true },
        { label: 'Realizado por', accessor: 'nombreCompleto', sortable: true },
        { 
          label: 'Rol', 
          accessor: 'rol', 
          sortable: true,
          render: (item) => (
            <td>{translatedRoles[item.rol] || item.rol}</td>
          ),
        },
        { label: 'Fecha', accessor: 'fecha', sortable: true },
        { 
          label: 'Descripción', 
          accessor: 'descripcion', 
          sortable: true,
          render: (item) => (
            <td>{translatedDescripciones[item.descripcion] || item.descripcion}</td>
          ),
        },
        { 
          label: 'Estado', 
          accessor: 'estadoNombre', 
          sortable: true,
          render: (item) => (
            <td>{translatedEstados[item.estadoNombre] || item.estadoNombre}</td>
          ),
        },
        { label: 'Observaciones', accessor: 'observaciones', sortable: true },
      ];
    }

    // Map activitytable to column format
    return admissionPage.activitytable
      .filter(item => item.visibility) // Only show visible columns
      .sort((a, b) => a.order - b.order) // Sort by order
      .map(item => {
        // Map the key to the correct accessor
        const accessorMap = {
          'id': 'actividadId',
          'createby': 'nombreCompleto',
          'rol': 'rol',
          'date': 'fecha',
          'description': 'descripcion',
          'state': 'estadoNombre',
          'observations': 'observaciones'
        };

        const accessor = accessorMap[item.key] || item.key;
        const column = {
          label: item.label,
          accessor: accessor,
          sortable: item.sortable
        };

        // Agregar render personalizado para los campos traducibles
        if (accessor === 'rol') {
          column.render = (item) => (
            <td>{translatedRoles[item.rol] || item.rol}</td>
          );
        } else if (accessor === 'descripcion') {
          column.render = (item) => (
            <td>{translatedDescripciones[item.descripcion] || item.descripcion}</td>
          );
        } else if (accessor === 'estadoNombre') {
          column.render = (item) => (
            <td>{translatedEstados[item.estadoNombre] || item.estadoNombre}</td>
          );
        }

        return column;
      });
    };
    return getColumns();
  }, [admissionPage, translatedRoles, translatedDescripciones, translatedEstados]);

  if (isLoadingContent || loading || isTranslating) {
    return (
      <div className="principal">
        <Skeleton.Input style={{ width: 300, height: 30 }} active />
        <div className="principal-container">
          <Skeleton active paragraph={{ rows: 8 }} />
          <Skeleton.Input style={{ width: '100%', height: 200 }} active />
        </div>
      </div>
    );
  }
  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setAdmissionPage(null);
      }}
    />
  } 

  return (
    <>
      {' '}
      <div className="principal">
        <h2 className={'ml-1'}>
          {admissionPage?.subtitle || 'Actividades de la Solicitud no.'} {state.numero}
        </h2>
        <div className={`principal-container`}>
          <OrderTable
            {...{ columns }}
            data={solicitudesActividades}
            clase="table-archivos"
          />
        </div>
      </div>
      <LoadingSpiner
        show={loading}
        fullPage={true}
        styles={{
          top: '45%',
          left: '50%',
          position: 'inherit',
          width: '50px',
          height: '50px',
          borderWidth: '10px',
          borderStyle: 'solid',
          borderColor: 'rgb(27 78 147) rgb(0 0 0) rgb(0 0 0)',
          borderImage: 'initial',
        }}
      />
    </>
  );
}

export default HistorialActividades;
